import React, { useMemo } from 'react'
import { Text } from '@chakra-ui/react'
import {
    AppointmentFieldsFragment,
    AppointmentStatus,
    RescheduleFieldsFragment,
    useSearchAppointmentsQuery,
} from '~graphql/generated/graphql'
import {
    addMinutes,
    areIntervalsOverlapping,
    format,
    startOfDay,
} from 'date-fns'
import { Box, HStack, Icon, SimpleGrid, Tooltip } from '@chakra-ui/react'
import {
    FaClock,
    FaExclamationTriangle,
    FaMedkit,
    FaPaw,
    FaStethoscope,
    FaStopwatch,
    FaUser,
} from 'react-icons/fa'
import {
    formatEmail,
    requiresCancelAndRebookRescheduling,
    stringIfExists,
} from '~utils/helpers'
import { useClinic } from '~contexts/clinic-context'
import {
    getBreedFromPatient,
    getClientNameFromAppointment,
    getClientPhoneFromAppointment,
    getPatientNameFromAppointment,
    getServiceNameFromAppointment,
    getSpeciesNameFromAppointment,
} from '~utils/appointment-helpers'
import { DEFAULT_TIME_ZONE } from '~config/constants'
import { formatInTimeZone } from 'date-fns-tz'
import SharedInfoText from '~components/shared/texts/shared-info-text'
import SharedStack from '~components/shared/wrappers/shared-stack'

interface Props {
    appointment: AppointmentFieldsFragment
    reschedule?: RescheduleFieldsFragment
    isRequestingChange?: boolean
}

const AppointmentOverview: React.FC<Props> = ({
    appointment,
    reschedule,
    isRequestingChange,
}) => {
    const { clinic } = useClinic()
    const conflictCheckStartTime =
        reschedule?.requested_start_time || appointment.start_time
    const conflictCheckEndTime = addMinutes(
        conflictCheckStartTime,
        appointment.minutes,
    )
    const date = startOfDay(conflictCheckStartTime)

    const clinicianToUse =
        reschedule?.requested_clinician || appointment.clinician
    const clinicianToUseId = clinicianToUse?.id
    const roomToUseId = appointment.room?.id

    const isRequestingNewClinician =
        reschedule?.requested_clinician &&
        reschedule?.requested_clinician?.name !== appointment.clinician?.name

    const { data: appointmentData } = useSearchAppointmentsQuery({
        variables: {
            searchInput: {
                clinicId: clinic?.id || '',
                start_date: startOfDay(date).getTime(),
                end_date: addMinutes(startOfDay(date), 24 * 60).getTime(),
                limit: 100,
            },
        },
        skip: !clinic?.id,
    })

    const usesRoomsToSchedule =
        !!clinic?.settings?.does_use_rooms_to_schedule

    const hasConflict = useMemo(() => {
        // Only treat room overlap as a conflict when the clinic schedules by
        // room *and* both sides have a concrete room id. Matching
        // `undefined === undefined` (null rooms) was falsely flagging every
        // room-less appointment on the day — common for Shepherd clinics.
        const otherAppts = appointmentData?.searchAppointments?.filter(
            appt => {
                if (appt.id === appointment.id) return false
                if (
                    appt.status === AppointmentStatus.Cancelled ||
                    appt.status === AppointmentStatus.Denied
                ) {
                    return false
                }

                const clinicianConflict =
                    !!clinicianToUseId &&
                    appt.clinician?.id === clinicianToUseId
                const roomConflict =
                    usesRoomsToSchedule &&
                    !!roomToUseId &&
                    appt.room?.id === roomToUseId

                return clinicianConflict || roomConflict
            },
        )

        return otherAppts?.some(appt =>
            areIntervalsOverlapping(
                {
                    start: appt.start_time,
                    end: addMinutes(appt.start_time, appt.minutes),
                },
                {
                    start: conflictCheckStartTime,
                    end: conflictCheckEndTime,
                },
            ),
        )
    }, [
        conflictCheckEndTime,
        conflictCheckStartTime,
        appointment.id,
        appointmentData?.searchAppointments,
        clinicianToUseId,
        roomToUseId,
        usesRoomsToSchedule,
    ])

    return (
        <Box>
            {hasConflict && (
                <Box
                    w="full"
                    rounded="md"
                    bg="yellow.100"
                    py={2}
                    px={4}
                    mb={4}
                    borderWidth={1}
                    borderColor="yellow.300"
                >
                    <HStack alignItems="center" mb={1}>
                        <Icon
                            as={FaExclamationTriangle}
                            fontSize="xl"
                            color="yellow.500"
                        />
                        <Text
                            fontSize="lg"
                            fontWeight="semibold"
                            color="yellow.500"
                        >
                            Schedule Overlap
                        </Text>
                    </HStack>
                    <Text>
                        This appointment has been requested at a time when there
                        is already an appointment in your PIMS.
                    </Text>
                </Box>
            )}
            <Box px={2}>
                <Text fontWeight="semibold" fontSize="lg" mb={2}>
                    {getServiceNameFromAppointment(appointment)?.capitalize()}{' '}
                    Appointment
                    {reschedule
                        ? ' Reschedule Request'
                        : isRequestingChange
                          ? ''
                          : ' Request'}
                </Text>
                <SimpleGrid
                    gridGap={4}
                    columns={{ base: 1, md: 3 }}
                    mb={4}
                    alignItems="flex-start"
                >
                    <HStack alignItems="flex-start">
                        <Icon as={FaPaw} color="gray.400" mt={1.5} />
                        <Box>
                            <Text fontWeight="medium">
                                {getPatientNameFromAppointment(appointment)}
                            </Text>
                            <Text color="gray.500">
                                {getSpeciesNameFromAppointment(appointment)}
                                {stringIfExists(
                                    getBreedFromPatient(appointment?.patient),
                                    { pretext: ', ' },
                                )}
                            </Text>
                        </Box>
                    </HStack>
                    <HStack alignItems="flex-start">
                        <Icon as={FaUser} color="gray.400" mt={1.5} />
                        <Box overflow="hidden">
                            <Text fontWeight="medium">
                                {getClientNameFromAppointment(appointment)}
                            </Text>
                            <Text
                                color="gray.500"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                                overflow="hidden"
                            >
                                {getClientPhoneFromAppointment(
                                    appointment,
                                    true,
                                ) || formatEmail(appointment?.client?.email)}
                            </Text>
                        </Box>
                    </HStack>

                    <HStack alignItems="flex-start">
                        <Icon as={FaStethoscope} color="gray.400" mt={1.5} />
                        <Box>
                            <Text fontWeight="medium">
                                {appointment.clinician?.name}
                            </Text>
                            <Text color="gray.500">
                                {appointment.room?.name}
                            </Text>
                        </Box>
                    </HStack>
                    <HStack alignItems="flex-start">
                        <Icon as={FaMedkit} color="gray.400" mt={1.5} />
                        <Box>
                            <Text fontWeight="medium">
                                {getServiceNameFromAppointment(appointment)}
                            </Text>
                            <Text color="gray.500">
                                {!!appointment.service
                                    ? appointment.service_name
                                    : ''}
                            </Text>
                        </Box>
                    </HStack>
                    {!isRequestingChange && (
                        <HStack alignItems="flex-start">
                            <Icon as={FaClock} color="gray.400" mt={1.5} />
                            <Box>
                                <Text fontWeight="medium">{`${format(
                                    appointment.start_time,
                                    "h:mm aaaaa'm'",
                                )} to ${format(
                                    addMinutes(
                                        appointment.start_time,
                                        appointment.minutes,
                                    ),
                                    "h:mm aaaaa'm'",
                                )}`}</Text>
                                <Text color="gray.500">
                                    {format(
                                        appointment.start_time,
                                        'LLLL do, yyyy',
                                    )}
                                </Text>
                            </Box>
                        </HStack>
                    )}
                    <HStack>
                        <Icon as={FaStopwatch} color="gray.400" />
                        <Text fontWeight="medium">{`${appointment.minutes} minutes`}</Text>
                    </HStack>
                </SimpleGrid>
            </Box>
            {reschedule && (
                <SharedStack spacing={2} mb={4}>
                    <Box
                        background="brand.50"
                        px={4}
                        py={2}
                        rounded="md"
                        border="1px solid"
                        borderColor="brand.500"
                        w="full"
                    >
                        <Text>
                            Requested change to new time{' '}
                            <Text as="span" fontWeight="semibold">
                                {format(
                                    reschedule.requested_start_time || 0,
                                    'h:mm a',
                                )}{' '}
                                -{' '}
                                {format(
                                    addMinutes(
                                        reschedule.requested_start_time || 0,
                                        appointment.minutes,
                                    ),
                                    "h:mm a 'on' LLLL do, yyyy",
                                )}
                            </Text>
                        </Text>
                        <SharedInfoText>
                            {`Reschedule requested on ${formatInTimeZone(
                                reschedule.created_at,
                                clinic?.time_zone ?? DEFAULT_TIME_ZONE,
                                'MMMM d, h:mm a',
                            )}`}
                        </SharedInfoText>
                    </Box>
                    {isRequestingNewClinician && (
                        <Box
                            background="brand.50"
                            px={4}
                            py={2}
                            rounded="md"
                            border="1px solid"
                            borderColor="brand.500"
                            w="full"
                        >
                            <Text>
                                Requested change to new clinician{' '}
                                <Text as="span" fontWeight="semibold">
                                    {reschedule.requested_clinician?.name}
                                </Text>
                            </Text>
                            <SharedInfoText>
                                {`Requested new clinician on ${formatInTimeZone(
                                    reschedule.created_at,
                                    clinic?.time_zone ?? DEFAULT_TIME_ZONE,
                                    'MMMM d, yyyy',
                                )}`}
                            </SharedInfoText>
                        </Box>
                    )}
                    {!isRequestingChange &&
                        requiresCancelAndRebookRescheduling(clinic) && (
                            <Box
                                background="yellow.100"
                                px={4}
                                py={2}
                                rounded="md"
                                border="1px solid"
                                borderColor="yellow.300"
                            >
                                <Text>
                                    The original appointment will be cancelled
                                    and rebooked at the requested time. If you
                                    would prefer to preserve the original
                                    appointment, please move the appointment
                                    manually in your PIMS and mark this action
                                    item as resolved.
                                </Text>
                            </Box>
                        )}
                </SharedStack>
            )}
        </Box>
    )
}

export default AppointmentOverview
